/**
 * @fileoverview Pagina principal de gestao de templates Meta (lista + CRUD).
 *
 * Seleciona automaticamente a primeira conexao Meta Cloud API da company.
 * Quando ha mais de uma, permite alternar via pill clicavel que abre um menu.
 */

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTheme } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  MoreVertical,
  RefreshCw,
  Sparkles,
  BarChart3,
  ChevronDown,
  Copy,
  ShieldOff,
} from "lucide-react";

import MainContainer from "../../components/MainContainer";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../../components/ConfirmationModal";
import usePermission from "../../hooks/usePermission";

import { useListStyles } from "./styles";
import {
  CATEGORY_LABELS,
  LANGUAGE_LABELS,
  getStatusStyle,
  getStatusLabel,
  getBodyText,
} from "./statusConfig";
import TemplateBuilderModal from "./TemplateBuilderModal";
import TemplateEditModal from "./TemplateEditModal";
import OptOutConfigModal from "./OptOutConfigModal";

const STATUS_FILTERS = [
  { key: "all", label: "Todos" },
  { key: "APPROVED", label: "Aprovados" },
  { key: "PENDING", label: "Em análise" },
  { key: "REJECTED", label: "Rejeitados" },
];

const MetaTemplates = () => {
  const classes = useListStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";
  const { whatsApps } = useContext(WhatsAppsContext);

  const metaConnections = useMemo(
    () => (whatsApps || []).filter((w) => w.provider === "meta-api"),
    [whatsApps]
  );

  const [selectedConnectionId, setSelectedConnectionId] = useState(null);
  const [connectionMenuOpen, setConnectionMenuOpen] = useState(false);
  const connectionPillRef = useRef(null);

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [builderOpen, setBuilderOpen] = useState(false);
  const [optOutOpen, setOptOutOpen] = useState(false);
  // O descadastro automático é dos disparos em massa: mass_send.manage, com a regra nova ligada.
  const canOptOut = usePermission().can("mass_send.manage", true);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [cardMenuId, setCardMenuId] = useState(null);

  useEffect(() => {
    if (metaConnections.length === 0) return;
    if (!selectedConnectionId) {
      setSelectedConnectionId(metaConnections[0].id);
    } else if (
      !metaConnections.find((c) => c.id === selectedConnectionId)
    ) {
      setSelectedConnectionId(metaConnections[0].id);
    }
  }, [metaConnections, selectedConnectionId]);

  const selectedConnection = useMemo(
    () => metaConnections.find((c) => c.id === selectedConnectionId),
    [metaConnections, selectedConnectionId]
  );

  const fetchTemplates = useCallback(async () => {
    if (!selectedConnectionId) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/meta/templates/${selectedConnectionId}/all`
      );
      setTemplates(data.templates || []);
    } catch (err) {
      toastError(err);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [selectedConnectionId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Close card menus on outside click.
  // IMPORTANTE: usamos `click` (nao `mousedown`) + checagem por data-attribute
  // para garantir que o onClick dos itens do popover (ex: "Ver estatisticas")
  // rode ANTES do handler global desmontar o menu.
  useEffect(() => {
    if (!cardMenuId && !connectionMenuOpen) return;
    const handler = (e) => {
      if (e.target.closest && e.target.closest("[data-meta-popover]")) {
        return;
      }
      if (
        connectionPillRef.current &&
        connectionPillRef.current.contains(e.target)
      ) {
        return;
      }
      setCardMenuId(null);
      setConnectionMenuOpen(false);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [cardMenuId, connectionMenuOpen]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return templates
      .filter((t) => {
        if (statusFilter !== "all" && t.status !== statusFilter) return false;
        if (!term) return true;
        return (
          t.name?.toLowerCase().includes(term) ||
          getBodyText(t).toLowerCase().includes(term)
        );
      })
      .sort((a, b) => {
        const statusOrder = { APPROVED: 0, PENDING: 1, REJECTED: 2 };
        const diff =
          (statusOrder[a.status] ?? 3) - (statusOrder[b.status] ?? 3);
        if (diff !== 0) return diff;
        return (a.name || "").localeCompare(b.name || "");
      });
  }, [templates, search, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts = { all: templates.length };
    templates.forEach((t) => {
      counts[t.status] = (counts[t.status] || 0) + 1;
    });
    return counts;
  }, [templates]);

  const handleOpenEdit = (template) => {
    setEditingTemplate(template);
    setCardMenuId(null);
  };

  const handleOpenDelete = (template) => {
    setPendingDelete(template);
    setConfirmOpen(true);
    setCardMenuId(null);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete || !selectedConnectionId) return;
    setDeleting(true);
    try {
      await api.delete(
        `/meta/templates/${selectedConnectionId}/${pendingDelete.id}`,
        {
          params: {
            name: pendingDelete.name,
            language: pendingDelete.language,
          },
        }
      );
      toast.success("Template removido com sucesso.");
      setConfirmOpen(false);
      setPendingDelete(null);
      fetchTemplates();
    } catch (err) {
      toastError(err);
    } finally {
      setDeleting(false);
    }
  };

  const handleDuplicate = async (template) => {
    setCardMenuId(null);
    setEditingTemplate(null);
    setBuilderOpen({ duplicateFrom: template });
  };

  const handleCreateSuccess = () => {
    setBuilderOpen(false);
    fetchTemplates();
  };

  const handleUpdateSuccess = () => {
    setEditingTemplate(null);
    fetchTemplates();
  };

  if (metaConnections.length === 0) {
    return (
      <MainContainer className={classes.root}>
        <div className={classes.header}>
          <div className={classes.headerLeft}>
            <div className={classes.eyebrow}>Administração</div>
            <div className={classes.title}>Templates Meta</div>
            <div className={classes.subtitle}>
              Gerencie os modelos oficiais do WhatsApp Business diretamente pela
              plataforma.
            </div>
          </div>
        </div>
        <div className={classes.emptyState}>
          <div className={classes.emptyIcon}>
            <Sparkles size={38} strokeWidth={1.25} />
          </div>
          <div className={classes.emptyTitle}>Nenhuma conexão Meta encontrada</div>
          <div className={classes.emptyText}>
            Para gerenciar templates você precisa primeiro conectar um número
            através do provedor oficial Meta Cloud API em Conexões.
          </div>
        </div>
      </MainContainer>
    );
  }

  return (
    <MainContainer className={classes.root}>
      <ConfirmationModal
        title={
          pendingDelete
            ? `Remover "${pendingDelete.name}"?`
            : "Remover template?"
        }
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setPendingDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        confirmLoading={deleting}
      >
        Esta ação remove o template da Meta e não pode ser desfeita. Se o template
        for aprovado, ele deixará de estar disponível para envio imediatamente.
      </ConfirmationModal>

      {builderOpen && (
        <TemplateBuilderModal
          open
          onClose={() => setBuilderOpen(false)}
          onSuccess={handleCreateSuccess}
          connectionId={selectedConnectionId}
          duplicateFrom={builderOpen.duplicateFrom}
        />
      )}

      {editingTemplate && (
        <TemplateEditModal
          open
          template={editingTemplate}
          connectionId={selectedConnectionId}
          onClose={() => setEditingTemplate(null)}
          onSuccess={handleUpdateSuccess}
        />
      )}

      {canOptOut && <OptOutConfigModal open={optOutOpen} onClose={() => setOptOutOpen(false)} />}

      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <div className={classes.eyebrow}>Administração</div>
          <div className={classes.title}>Templates Meta</div>
          <div className={classes.subtitle}>
            Crie, edite e acompanhe o status dos seus modelos oficiais do
            WhatsApp Business Platform.
          </div>
        </div>
        <div className={classes.headerRight}>
          <button
            type="button"
            className={classes.ghostButton}
            onClick={fetchTemplates}
            disabled={loading}
            title="Atualizar"
          >
            <RefreshCw
              size={15}
              strokeWidth={1.5}
              style={{
                transition: "transform 0.4s ease",
                transform: loading ? "rotate(180deg)" : "none",
              }}
            />
            Atualizar
          </button>
          {canOptOut && (
            <button
              type="button"
              className={classes.ghostButton}
              onClick={() => setOptOutOpen(true)}
              title="Configurar descadastro automático"
            >
              <ShieldOff size={15} strokeWidth={1.5} />
              Descadastro
            </button>
          )}
          <button
            type="button"
            className={classes.primaryButton}
            onClick={() => setBuilderOpen(true)}
          >
            <Plus size={16} strokeWidth={2} />
            Novo template
          </button>
        </div>
      </div>

      <div className={classes.toolbar}>
        {metaConnections.length > 1 ? (
          <div
            style={{ position: "relative" }}
            ref={connectionPillRef}
            data-meta-popover="true"
          >
            <div
              className={classes.connectionPill}
              onClick={() => setConnectionMenuOpen((v) => !v)}
              role="button"
            >
              <span>{selectedConnection?.name || "Selecionar conexão"}</span>
              <ChevronDown size={13} strokeWidth={2} />
            </div>
            {connectionMenuOpen && (
              <div
                className={classes.popoverMenu}
                style={{ top: 42, right: "auto", left: 0 }}
              >
                {metaConnections.map((c) => (
                  <div
                    key={c.id}
                    className={classes.popoverItem}
                    onClick={() => {
                      setSelectedConnectionId(c.id);
                      setConnectionMenuOpen(false);
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background:
                          c.id === selectedConnectionId ? "#22c55e" : "#cbd5e1",
                      }}
                    />
                    {c.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className={classes.connectionPill} style={{ cursor: "default" }}>
            {selectedConnection?.name || "—"}
          </div>
        )}

        <div className={classes.searchBox}>
          <Search
            size={16}
            strokeWidth={1.5}
            color={isDark ? "rgba(255,255,255,0.4)" : "#94a3b8"}
          />
          <input
            className={classes.searchInput}
            placeholder="Buscar por nome ou conteúdo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className={classes.filterGroup}>
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`${classes.filterChip} ${
                statusFilter === f.key ? classes.filterChipActive : ""
              }`}
              onClick={() => setStatusFilter(f.key)}
            >
              {f.label}
              {statusCounts[f.key] > 0 && (
                <span style={{ opacity: 0.55, marginLeft: 6 }}>
                  {statusCounts[f.key]}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {loading && templates.length === 0 ? (
        <div className={classes.loadingBox}>Carregando templates...</div>
      ) : filtered.length === 0 ? (
        <div className={classes.emptyState}>
          <div className={classes.emptyIcon}>
            <Sparkles size={38} strokeWidth={1.25} />
          </div>
          <div className={classes.emptyTitle}>
            {templates.length === 0
              ? "Nenhum template ainda"
              : "Nada por aqui"}
          </div>
          <div className={classes.emptyText}>
            {templates.length === 0
              ? "Crie seu primeiro modelo oficial. Após aprovado pela Meta, você poderá disparar em massa ou em conversas individuais."
              : "Ajuste os filtros ou a busca para encontrar o que procura."}
          </div>
        </div>
      ) : (
        <div className={classes.grid}>
          {filtered.map((t) => {
            const statusStyle = getStatusStyle(t.status, isDark);
            const body = getBodyText(t);
            return (
              <div
                key={t.id || t.name}
                className={classes.card}
                onClick={() => handleOpenEdit(t)}
              >
                <div className={classes.cardHeader}>
                  <div className={classes.cardTitleBlock}>
                    <div className={classes.cardName} title={t.name}>
                      {t.name}
                    </div>
                    <div className={classes.cardMetaRow}>
                      <span className={classes.cardCategory}>
                        {CATEGORY_LABELS[t.category] || t.category}
                      </span>
                      <span className={classes.cardDot} />
                      <span className={classes.cardLang}>
                        {LANGUAGE_LABELS[t.language] || t.language}
                      </span>
                    </div>
                  </div>
                  {statusStyle && (
                    <span
                      className={classes.statusPill}
                      style={{
                        background: statusStyle.bg,
                        color: statusStyle.fg,
                        border: `1px solid ${statusStyle.border}`,
                      }}
                    >
                      <span
                        className={classes.statusDot}
                        style={{ background: statusStyle.fg }}
                      />
                      {getStatusLabel(t.status)}
                    </span>
                  )}
                </div>

                <div className={classes.cardBody}>
                  {body || (
                    <span style={{ opacity: 0.45 }}>
                      (sem corpo de texto — usando mídia ou botões)
                    </span>
                  )}
                </div>

                <div className={classes.cardFooter}>
                  <div style={{ position: "relative" }} data-meta-popover="true">
                    <button
                      type="button"
                      className={classes.iconBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCardMenuId(
                          cardMenuId === (t.id || t.name)
                            ? null
                            : t.id || t.name
                        );
                      }}
                    >
                      <MoreVertical size={15} strokeWidth={1.5} />
                    </button>
                    {cardMenuId === (t.id || t.name) && (
                      <div
                        className={classes.popoverMenu}
                        style={{ top: 36, right: "auto", left: 0 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          className={classes.popoverItem}
                          onClick={() => handleDuplicate(t)}
                        >
                          <Copy size={14} strokeWidth={1.5} />
                          Duplicar
                        </div>
                        {t.status === "APPROVED" && (
                          <div
                            className={classes.popoverItem}
                            onClick={() => {
                              setCardMenuId(null);
                              setEditingTemplate({ ...t, initialTab: "analytics" });
                            }}
                          >
                            <BarChart3 size={14} strokeWidth={1.5} />
                            Ver estatísticas
                          </div>
                        )}
                        <div
                          className={`${classes.popoverItem} ${classes.popoverItemDanger}`}
                          onClick={() => handleOpenDelete(t)}
                        >
                          <Trash2 size={14} strokeWidth={1.5} />
                          Remover
                        </div>
                      </div>
                    )}
                  </div>

                  <div className={classes.actionRow}>
                    <button
                      type="button"
                      className={classes.iconBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenEdit(t);
                      }}
                      title="Editar"
                    >
                      <Pencil size={14} strokeWidth={1.5} />
                    </button>
                    <button
                      type="button"
                      className={`${classes.iconBtn} ${classes.iconBtnDanger}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDelete(t);
                      }}
                      title="Remover"
                    >
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </MainContainer>
  );
};

export default MetaTemplates;
